<template>
  <div class="my-address-info-area">
    <div class="c-round-box">
      <strong class="subject">기본 배송지</strong>
      <div v-if="basicAddress" class="text-wrap">
        <p class="text">
          <b class="color-primary">[{{ basicAddress.dlvdesNm }}]</b>
          {{ basicAddress.acptrNm }}({{ basicAddress.zip }})
        </p>
        <p class="text">
          {{ basicAddress.roadNmBscAddr }} {{ basicAddress.roadNmDtlAddr }}
          <br class="only-mo" />
          (연락처: {{ basicAddress.acptrWrdTelno }})
        </p>
      </div>
    </div>
    <!-- D: PC 전용 -->
    <div class="c-btn-area flex-ar mt24">
      <button
        type="button"
        class="c-util-button type-line-black type-small"
        @click="showEditPopForAdd"
      >
        <span>배송지 추가</span>
      </button>
    </div>

    <div class="scroll-x only-pc">
      <div class="scroll-x-inner">
        <div class="c-table" style="width: 1102px">
          <table>
            <caption>
              개인정보에 관한 민원서비스 테이블
            </caption>
            <colgroup>
              <col style="width: 140px" />
              <col style="width: 100px" />
              <col />
              <col style="width: 180px" />
              <col style="width: 140px" />
            </colgroup>
            <thead>
              <tr>
                <th class="ac">주소지 별칭</th>
                <th class="ac">받는분</th>
                <th class="ac">배송 주소</th>
                <th class="ac">연락처</th>
                <th class="ac">수정/삭제</th>
              </tr>
            </thead>
            <tbody>
              <AddressRow
                v-for="item in list"
                :key="item.dlvdesSn"
                :address="item"
                @edit="(address) => showEditPopForEdit(address)"
                @delete="(address) => deleteAddress(address)"
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- D : pageing area -->
    <Pagination
      ref="paginationEl"
      v-if="!isNodata && pageData"
      :total-pages="pageData.totalPage"
      :per-page="pageData.limit"
      :show-select="true"
      @page-change="onPageChange"
      @items-per-page-change="onPagePerPageChange"
    />
    <!-- // D : pageing area -->
    <!-- // D: PC 전용 -->
    <!-- D: MO 전용 -->
    <div class="address-select-list only-mo">
      <ul>
        <MobileAddressRow
          v-for="item in mobileList"
          :key="item.dlvdesSn"
          :address="item"
          @edit="(address) => showEditPopForEdit(address)"
          @delete="(address) => deleteAddress(address)"
        />
      </ul>
    </div>
    <div class="c-btn-area type-full is-fixed" data-bottom-fixed>
      <button
        type="button"
        class="c-mo-button type-primary type-large"
        @click="showEditPopForAdd"
      >
        <span class="bold">배송지 추가</span>
      </button>
    </div>
    <!-- // D: MO 전용 -->
  </div>
  <AddressEditPop
    v-if="isShowEditPop"
    v-bind="editPopProps"
    @close="() => (isShowEditPop = false)"
    @updated="onAddressUpdated"
  />
</template>
<script setup name="delivMngTab">
import Pagination from "@/components/paintpro/base/pagination.vue";
import { computed, inject, onBeforeUnmount, onMounted, ref } from "vue";
import { api } from "@/plugins/api";
import AddressRow from "./delivMngTabRow.vue";
import AddressEditPop from "@/components/paintpro/agency/agencyDeliveryAddressEditPop.vue";
import MobileAddressRow from "./delivMngTabMoRow.vue";
import * as cmUtil from "@/utils/cmUtil.js";
const { isMobile } = cmUtil.useDeviceType();

const url = inject("url_prefix");

const searchParams = ref({
  page: 1,
  limit: 20,
});
const pageData = ref(null);
const list = ref([]);
const mobileList = ref([]);
const scrollSensed = ref(false); // 스크롤 중복 감지를 막기 위한 변수입니다.

const basicAddress = ref(null);

const isShowEditPop = ref(false);
const editPopProps = ref({
  address: null,
});

const paginationEl = ref(null);

onMounted(() => {
  loadBasicAddress();
  loadSearchData();
  window.addEventListener("scroll", onScrollEvent, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScrollEvent);
});

const isNodata = computed(() => {
  return list.value.length === 0;
});

const onPageChange = (page) => {
  searchParams.value.page = page;
  loadSearchData();
};

const onPagePerPageChange = (limit) => {
  searchParams.value.page = 1;
  searchParams.value.limit = limit;
  paginationEl.value?.setCurrentPage(1);
  loadSearchData();
};

const loadBasicAddress = async () => {
  const res = await api.get(`${url}/agency/delivery/address/basic`);

  const { resultCode } = res;
  if (resultCode !== 200) {
    return;
  }

  basicAddress.value = res.data;
};

const loadSearchData = async () => {
  const res = await api.post(`${url}/agency/delivery/address/search`, {
    ...searchParams.value,
  });

  const { resultCode } = res;
  if (resultCode !== 200) {
    return;
  }

  pageData.value = res.data;
  list.value = [...pageData.value.list];
  mobileList.value.push(...pageData.value.list);
};

const loadNextPage = () => {
  if (!pageData.value) {
    // 페이지 정보가 로드되지 않은 상태에서는 다음페이지를 로드하지 않습니다.
    return;
  }

  if (pageData.value.currPage >= pageData.value.totalPage) {
    return;
  }

  searchParams.value.page += 1;

  loadSearchData();
};

const showEditPopForAdd = () => {
  editPopProps.value.address = null;
  isShowEditPop.value = true;
};

const showEditPopForEdit = (address) => {
  editPopProps.value.address = address;
  isShowEditPop.value = true;
};

const deleteAddress = async (address) => {
  if (
    !(await cmUtil.confirm(`${address.dlvdesNm} 배송지를 삭제하시겠습니까?`))
  ) {
    return;
  }

  const res = await api.post(
    `${url}/agency/delivery/address/${address.dlvdesSn}/delete`
  );

  const { resultCode } = res;
  if (resultCode !== 200) {
    return;
  }

  await cmUtil.alert("배송지가 삭제되었습니다.");

  if (isMobile.value) {
    mobileList.value = [];
  }
  loadSearchData();
};

const onAddressUpdated = () => {
  if (isMobile.value) {
    mobileList.value = [];
  }
  loadSearchData();
};

const onScrollEvent = () => {
  if (scrollSensed.value) return;
  if (!isMobile.value) return;

  const scrollTop = Math.ceil(document.documentElement.scrollTop);
  const scrollHeight = document.documentElement.scrollHeight;
  const clientHeight = document.documentElement.clientHeight;

  const isBottom = scrollTop + clientHeight >= scrollHeight ? true : false;

  if (isBottom) {
    loadNextPage();

    scrollSensed.value = true;
    setTimeout(() => {
      scrollSensed.value = false;
    }, 50);
  }
};
</script>
<style scoped></style>
